import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Trash2, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Separator } from "@/components/ui/separator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AddSplitsModal({ open, onOpenChange, policy, onSuccess }) {
  const [step, setStep] = useState(1); // 1: input, 2: summary
  const [mainAgentPercent, setMainAgentPercent] = useState('');
  const [splits, setSplits] = useState([]);
  const [currentNPN, setCurrentNPN] = useState('');
  const [currentPercent, setCurrentPercent] = useState('');
  const [verifyingNPN, setVerifyingNPN] = useState(false);
  const [verifiedAgent, setVerifiedAgent] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const getTotalAllocated = () => {
    const mainPercent = parseFloat(mainAgentPercent) || 0;
    const splitsTotal = splits.reduce((sum, s) => sum + parseFloat(s.percent), 0);
    return mainPercent + splitsTotal;
  };

  const getRemainingPercent = () => {
    return 100 - getTotalAllocated();
  };

  const handleVerifyNPN = async () => {
    if (!currentNPN || currentNPN.length < 5) {
      toast.error('Please enter a valid NPN');
      return;
    }

    setVerifyingNPN(true);
    try {
      const response = await axios.post(`${API}/auth/verify-npn`, {
        npn: currentNPN
      });

      if (response.data.status === 'active') {
        const fullName = `${response.data.first_name || ''} ${response.data.last_name || ''}`.trim() || 'Agent';
        setVerifiedAgent({
          npn: currentNPN,
          name: fullName,
          firstName: response.data.first_name,
          lastName: response.data.last_name,
          status: 'active'
        });
        toast.success('NPN verified successfully!');
      } else {
        toast.error('This NPN is not active. Please contact support.');
        setVerifiedAgent(null);
      }
    } catch (err) {
      console.error('NPN verification error:', err);
      toast.error('NPN not found or disconnected. Please contact support.');
      setVerifiedAgent(null);
    } finally {
      setVerifyingNPN(false);
    }
  };

  const handleAddSplit = () => {
    if (!verifiedAgent) {
      toast.error('Please verify the NPN first');
      return;
    }

    if (!currentPercent || parseFloat(currentPercent) <= 0) {
      toast.error('Please enter a valid percentage');
      return;
    }

    const percent = parseFloat(currentPercent);
    const newTotal = getTotalAllocated() + percent;

    if (newTotal > 100) {
      toast.error(`Cannot add split. Would exceed 100% (currently at ${getTotalAllocated()}%)`);
      return;
    }

    setSplits([...splits, {
      npn: verifiedAgent.npn,
      name: verifiedAgent.name,
      percent: percent
    }]);

    // Reset form
    setCurrentNPN('');
    setCurrentPercent('');
    setVerifiedAgent(null);
    toast.success('Split added!');
  };

  const handleRemoveSplit = (index) => {
    setSplits(splits.filter((_, i) => i !== index));
  };

  const handleProceedToSummary = () => {
    if (!mainAgentPercent || parseFloat(mainAgentPercent) <= 0) {
      toast.error('Please enter your split percentage');
      return;
    }

    if (splits.length === 0) {
      toast.error('Please add at least one split agent');
      return;
    }

    const total = getTotalAllocated();
    if (total !== 100) {
      toast.error(`Total allocation must be 100% (currently ${total}%)`);
      return;
    }

    setStep(2);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        policy_id: policy.id,
        splits: splits.map(s => ({
          main_agent_split_percent: parseFloat(mainAgentPercent),
          agent_onsplit_npn: s.npn,
          agent_on_split_percent: s.percent
        }))
      };

      await axios.post(`${API}/portal/split/add`, payload);
      toast.success('Splits added successfully!');
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      console.error('Error adding splits:', err);
      toast.error('Failed to add splits. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setMainAgentPercent('');
    setSplits([]);
    setCurrentNPN('');
    setCurrentPercent('');
    setVerifiedAgent(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">Add Policy Splits</DialogTitle>
          <DialogDescription>
            {step === 1 ? 'Configure split percentages for this policy' : 'Review and confirm split allocation'}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-6">
            {/* Main Agent Percentage */}
            <div>
              <Label className="text-sm font-medium">Your Split Percentage *</Label>
              <Input
                type="number"
                placeholder="Enter your percentage"
                value={mainAgentPercent}
                onChange={(e) => setMainAgentPercent(e.target.value)}
                min="0"
                max="100"
                className="mt-2 rounded-xl"
              />
            </div>

            <Separator />

            {/* Progress Bar */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="text-sm font-medium">Total Allocation</Label>
                <Badge className={getTotalAllocated() === 100 ? "bg-green-500" : getTotalAllocated() > 100 ? "bg-red-500" : "bg-yellow-500"}>
                  {getTotalAllocated()}%
                </Badge>
              </div>
              <Progress value={getTotalAllocated()} className="h-3" />
              <p className="text-xs text-neutral-500 mt-1">Remaining: {getRemainingPercent()}%</p>
            </div>

            {/* Added Splits */}
            {splits.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Added Splits ({splits.length})</Label>
                {splits.map((split, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-neutral-50 rounded-xl">
                    <div>
                      <p className="text-sm font-medium text-neutral-800">{split.name}</p>
                      <p className="text-xs text-neutral-500">NPN: {split.npn}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="border-[#d4af37] text-[#b8941e]">
                        {split.percent}%
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveSplit(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add New Split */}
            {getRemainingPercent() > 0 && (
              <div className="space-y-4 p-4 border-2 border-dashed border-neutral-300 rounded-xl">
                <Label className="text-sm font-medium">Add Split Agent</Label>
                
                <div className="space-y-2">
                  <Label className="text-xs text-neutral-600">Agent NPN *</Label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      placeholder="Enter NPN"
                      value={currentNPN}
                      onChange={(e) => {
                        setCurrentNPN(e.target.value);
                        setVerifiedAgent(null);
                      }}
                      className="rounded-xl"
                    />
                    <Button
                      onClick={handleVerifyNPN}
                      disabled={verifyingNPN || !currentNPN}
                      className="bg-[#d4af37] hover:bg-[#b8941e] text-white rounded-xl"
                    >
                      {verifyingNPN ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Verify'
                      )}
                    </Button>
                  </div>
                  {verifiedAgent && (
                    <div className="flex items-center gap-2 text-green-700 text-sm bg-green-50 px-3 py-2 rounded-lg border border-green-200">
                      <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                      <span>
                        Verified: <strong>{verifiedAgent.name}</strong> (NPN: {verifiedAgent.npn})
                      </span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-neutral-600">Split Percentage *</Label>
                  <Input
                    type="number"
                    placeholder="Enter percentage"
                    value={currentPercent}
                    onChange={(e) => setCurrentPercent(e.target.value)}
                    min="0"
                    max={getRemainingPercent()}
                    className="rounded-xl"
                  />
                </div>

                <Button
                  onClick={handleAddSplit}
                  disabled={!verifiedAgent || !currentPercent}
                  variant="outline"
                  className="w-full border-[#d4af37] text-[#b8941e] hover:bg-[#f4e5b8] rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Split
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary */}
            <div className="bg-neutral-50 p-4 rounded-xl space-y-3">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-neutral-700">Main Agent (Owner)</p>
                  <p className="text-xs text-neutral-500">{policy.agentOwner_name || policy.policyAgent_owner}</p>
                </div>
                <Badge className="bg-[#d4af37] text-white">{mainAgentPercent}%</Badge>
              </div>
              
              <Separator />

              {splits.map((split, index) => (
                <div key={index} className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-neutral-700">{split.name}</p>
                    <p className="text-xs text-neutral-500">NPN: {split.npn}</p>
                  </div>
                  <Badge variant="outline" className="border-[#d4af37] text-[#b8941e]">
                    {split.percent}%
                  </Badge>
                </div>
              ))}

              <Separator />

              <div className="flex justify-between items-center">
                <p className="font-semibold text-neutral-800">Total Allocated</p>
                <Badge className="bg-green-500 text-white">100%</Badge>
              </div>
            </div>

            <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-xl">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <p className="text-sm text-blue-800">
                Once submitted, these splits will be applied to policy #{policy.policy_number}
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 1 ? (
            <>
              <Button variant="outline" onClick={handleClose} className="rounded-xl">
                Cancel
              </Button>
              <Button
                onClick={handleProceedToSummary}
                disabled={getTotalAllocated() !== 100 || splits.length === 0}
                className="bg-[#d4af37] hover:bg-[#b8941e] text-white rounded-xl"
              >
                Continue to Summary
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep(1)} className="rounded-xl">
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-[#d4af37] hover:bg-[#b8941e] text-white rounded-xl"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Splits'
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}